/**
 * Admin edit dialog for a Client (borrower).
 *
 * Tabs:
 *   • Company        — name, reg no, VAT, tax no, industry
 *   • Contact        — person, email, phone, physical/postal address
 *   • Banking        — bank details for repayments
 *   • Financial      — credit limit, facility limit, default interest rate
 *   • Notes          — admin free text
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Save, Mail, Upload, Trash2, FileText, ShieldCheck } from 'lucide-react';
import ChangeEmailDialog from './ChangeEmailDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const empty = {
  company_name: '', company_reg_number: '', vat_number: '', tax_number: '', industry_sector: '',
  contact_person: '', contact_email: '', contact_phone: '',
  physical_address: '', postal_address: '',
  bank_name: '', bank_branch_code: '', bank_account_number: '', bank_account_type: '', swift_code: '',
  credit_limit: '', facility_limit: '', interest_rate: '',
  notes: '',
};

const KYC_DOC_TYPES = [
  { key: 'id_doc', label: 'ID / Passport' },
  { key: 'proof_of_address', label: 'Proof of address' },
  { key: 'bank_letter', label: 'Bank confirmation letter' },
  { key: 'tax_clearance', label: 'Tax clearance' },
  { key: 'company_reg', label: 'Company registration (CIPC)' },
  { key: 'authorised_signatory', label: 'Authorised signatory resolution' },
  { key: 'source_of_funds', label: 'Source of funds' },
  { key: 'other', label: 'Other' },
];

export default function EditClientDialog({ open, onOpenChange, client, onSaved }) {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [tab, setTab] = useState('company');
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [liveEmail, setLiveEmail] = useState(client?.contact_email || client?.email || '');
  const [changeEmailOpen, setChangeEmailOpen] = useState(false);
  const [linkedUserId, setLinkedUserId] = useState(null);
  const [kycDocs, setKycDocs] = useState([]);
  const [kycUploading, setKycUploading] = useState(false);
  const [kycDocType, setKycDocType] = useState('id_doc');

  const loadKyc = useCallback(async (userId) => {
    if (!userId) { setKycDocs([]); return; }
    try {
      const kr = await axios.get(`${API_URL}/api/admin/users/${userId}/kyc-documents`, { headers });
      setKycDocs(kr.data.documents || []);
    } catch { /* non-fatal */ }
  }, [headers]);

  const load = useCallback(async () => {
    if (!client?.client_id) return;
    try {
      const r = await axios.get(`${API_URL}/api/admin/clients/${client.client_id}/detail`, { headers });
      const payload = r.data || {};
      const c = payload.profile || payload.client || payload;
      const linkedUserEmail = payload.user?.email || '';
      setLinkedUserId(payload.user?.id || null);
      loadKyc(payload.user?.id || null);
      setForm({
        company_name: c.company_name || '', company_reg_number: c.company_reg_number || '',
        vat_number: c.vat_number || '', tax_number: c.tax_number || '', industry_sector: c.industry_sector || '',
        contact_person: c.contact_person || '', contact_email: c.contact_email || '', contact_phone: c.contact_phone || '',
        physical_address: c.physical_address || '', postal_address: c.postal_address || '',
        bank_name: c.bank_name || '', bank_branch_code: c.bank_branch_code || '',
        bank_account_number: c.bank_account_number || '', bank_account_type: c.bank_account_type || '',
        swift_code: c.swift_code || '',
        credit_limit: c.credit_limit ?? '', facility_limit: c.facility_limit ?? '', interest_rate: c.interest_rate ?? '',
        notes: c.notes || '',
      });
      // Change-email button targets the LINKED users row — the CRM
      // contact_email may be blank for a linked client, so we always
      // fall through to the actual login email from the users record.
      // (Empty when the client isn't linked to a portal user at all —
      // in that case the Change-email button correctly stays hidden.)
      setLiveEmail(linkedUserEmail || c.contact_email || client?.contact_email || client?.email || '');
    } catch { /* fall back to what we have */
      setForm({ ...empty, company_name: client.company_name || '' });
      setLiveEmail(client?.contact_email || client?.email || '');
    }
  }, [client?.client_id, token, client, headers, loadKyc]);

  useEffect(() => { if (open) { setTab('company'); setLiveEmail(client?.contact_email || client?.email || ''); load(); } }, [open, load, client]);

  const save = async () => {
    if (!client?.client_id) return;
    setSaving(true);
    try {
      const body = Object.fromEntries(
        Object.entries(form)
          .filter(([_, v]) => v !== '' && v !== null && v !== undefined)
          .map(([k, v]) => {
            if (['credit_limit', 'facility_limit', 'interest_rate'].includes(k)) return [k, parseFloat(v)];
            return [k, v];
          })
      );
      await axios.put(`${API_URL}/api/admin/clients/${client.client_id}`, body, { headers });
      toast.success('Client updated');
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const uploadKyc = async (file) => {
    if (!file || !linkedUserId) return;
    setKycUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      await axios.post(
        `${API_URL}/api/admin/users/${linkedUserId}/kyc-documents?document_type=${encodeURIComponent(kycDocType)}`,
        fd,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } },
      );
      toast.success('KYC uploaded');
      loadKyc(linkedUserId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'KYC upload failed');
    } finally { setKycUploading(false); }
  };

  const deleteKyc = async (docId) => {
    if (!window.confirm('Delete this KYC document? Action is logged in the audit trail.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/kyc-documents/${docId}`, { headers });
      toast.success('Deleted');
      loadKyc(linkedUserId);
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  if (!client) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="edit-client-dialog">
        <DialogHeader>
          <DialogTitle>Edit client — {client.company_name}</DialogTitle>
          <DialogDescription>
            <span className="font-mono text-xs mr-2">{client.client_id}</span>
            {liveEmail && (
              <>
                <span className="font-mono text-xs mr-2" data-testid="edit-client-email">{liveEmail}</span>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-6 text-[11px] px-2 py-0"
                  onClick={() => setChangeEmailOpen(true)}
                  data-testid="open-change-client-email-btn"
                >
                  <Mail className="h-3 w-3 mr-1" /> Change email
                </Button>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-6">
            <TabsTrigger value="company" data-testid="client-tab-company">Company</TabsTrigger>
            <TabsTrigger value="contact" data-testid="client-tab-contact">Contact</TabsTrigger>
            <TabsTrigger value="banking" data-testid="client-tab-banking">Banking</TabsTrigger>
            <TabsTrigger value="financial" data-testid="client-tab-financial">Financial</TabsTrigger>
            <TabsTrigger value="kyc" data-testid="client-tab-kyc">KYC</TabsTrigger>
            <TabsTrigger value="notes" data-testid="client-tab-notes">Notes</TabsTrigger>
          </TabsList>

          <TabsContent value="company" className="grid grid-cols-1 md:grid-cols-2 gap-3 py-3">
            <div className="md:col-span-2"><Label>Company name</Label><Input value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} /></div>
            <div><Label>Registration no.</Label><Input value={form.company_reg_number} onChange={e => setForm({...form, company_reg_number: e.target.value})} /></div>
            <div><Label>VAT no.</Label><Input value={form.vat_number} onChange={e => setForm({...form, vat_number: e.target.value})} /></div>
            <div><Label>Tax no.</Label><Input value={form.tax_number} onChange={e => setForm({...form, tax_number: e.target.value})} /></div>
            <div><Label>Industry / sector</Label><Input value={form.industry_sector} onChange={e => setForm({...form, industry_sector: e.target.value})} placeholder="e.g. Mining, Agriculture" /></div>
          </TabsContent>

          <TabsContent value="contact" className="grid grid-cols-1 md:grid-cols-2 gap-3 py-3">
            <div><Label>Contact person</Label><Input value={form.contact_person} onChange={e => setForm({...form, contact_person: e.target.value})} /></div>
            <div><Label>Contact email</Label><Input type="email" value={form.contact_email} onChange={e => setForm({...form, contact_email: e.target.value})} /></div>
            <div><Label>Contact phone</Label><Input value={form.contact_phone} onChange={e => setForm({...form, contact_phone: e.target.value})} /></div>
            <div className="md:col-span-2"><Label>Physical address</Label><Textarea rows={3} value={form.physical_address} onChange={e => setForm({...form, physical_address: e.target.value})} /></div>
            <div className="md:col-span-2"><Label>Postal address</Label><Textarea rows={3} value={form.postal_address} onChange={e => setForm({...form, postal_address: e.target.value})} placeholder="Leave blank if same as physical" /></div>
          </TabsContent>

          <TabsContent value="banking" className="grid grid-cols-1 md:grid-cols-2 gap-3 py-3">
            <div><Label>Bank</Label><Input value={form.bank_name} onChange={e => setForm({...form, bank_name: e.target.value})} /></div>
            <div><Label>Branch code</Label><Input value={form.bank_branch_code} onChange={e => setForm({...form, bank_branch_code: e.target.value})} /></div>
            <div><Label>Account number</Label><Input value={form.bank_account_number} onChange={e => setForm({...form, bank_account_number: e.target.value})} /></div>
            <div>
              <Label>Account type</Label>
              <Select value={form.bank_account_type || '__none__'} onValueChange={v => setForm({...form, bank_account_type: v === '__none__' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— none —</SelectItem>
                  <SelectItem value="Cheque">Cheque / Current</SelectItem>
                  <SelectItem value="Savings">Savings</SelectItem>
                  <SelectItem value="Transmission">Transmission</SelectItem>
                  <SelectItem value="Business">Business</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2"><Label>SWIFT / BIC</Label><Input value={form.swift_code} onChange={e => setForm({...form, swift_code: e.target.value.toUpperCase()})} /></div>
          </TabsContent>

          <TabsContent value="financial" className="grid grid-cols-1 md:grid-cols-2 gap-3 py-3">
            <div><Label>Credit limit (ZAR)</Label><Input type="number" value={form.credit_limit} onChange={e => setForm({...form, credit_limit: e.target.value})} /></div>
            <div><Label>Facility limit (ZAR)</Label><Input type="number" value={form.facility_limit} onChange={e => setForm({...form, facility_limit: e.target.value})} /></div>
            <div><Label>Default interest rate (% p.a.)</Label><Input type="number" step="0.01" value={form.interest_rate} onChange={e => setForm({...form, interest_rate: e.target.value})} /></div>
          </TabsContent>

          <TabsContent value="kyc" className="py-3">
            {!linkedUserId ? (
              <div className="text-sm text-[#6C757D] italic p-4 bg-slate-50 rounded">
                This client has no portal login yet, so there's nowhere to attach KYC documents. Create a portal
                account for them first (Role: Client) if you need to upload KYC on their behalf.
              </div>
            ) : (
              <div>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <h4 className="font-semibold text-[#1D2D2A] flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4" /> KYC documents ({kycDocs.filter(d => d.is_current).length} current)
                  </h4>
                  <div className="flex items-center gap-2">
                    <Select value={kycDocType} onValueChange={setKycDocType}>
                      <SelectTrigger className="w-56" data-testid="client-kyc-doc-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {KYC_DOC_TYPES.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <label className={`inline-flex items-center gap-1 px-3 py-2 rounded-md border cursor-pointer text-sm ${kycUploading ? 'opacity-50 pointer-events-none' : 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700'}`} data-testid="client-kyc-upload-btn">
                      <Upload className="h-4 w-4" />
                      {kycUploading ? 'Uploading…' : 'Upload'}
                      <input type="file" hidden onChange={e => { const f = e.target.files?.[0]; if (f) uploadKyc(f); e.target.value=''; }} />
                    </label>
                  </div>
                </div>
                {kycDocs.length === 0 ? (
                  <div className="text-xs text-[#6C757D] italic p-4 bg-slate-50 rounded">No KYC docs on file yet.</div>
                ) : (
                  <div className="space-y-1 max-h-64 overflow-y-auto">
                    {kycDocs.map(d => (
                      <div key={d.id} className={`flex items-center justify-between bg-white border rounded px-3 py-2 text-sm ${!d.is_current ? 'opacity-60' : ''}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="h-4 w-4 text-emerald-700 shrink-0" />
                          <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline truncate">{d.file_name}</a>
                          <Badge variant="outline" className="text-[10px]">{d.document_type}</Badge>
                          {!d.is_current && <Badge variant="outline" className="text-[10px]">v{d.version} (superseded)</Badge>}
                          {d.status && <Badge variant="outline" className="text-[10px]">{d.status}</Badge>}
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="text-xs text-[#6C757D]">{new Date(d.uploaded_at).toLocaleDateString()}</span>
                          <button onClick={() => deleteKyc(d.id)} className="text-red-600 hover:text-red-700" data-testid={`client-kyc-delete-${d.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="notes" className="py-3">
            <Label>Notes (admin only)</Label>
            <Textarea rows={8} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Internal notes — not visible to the client" />
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Close</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="edit-client-save">
            <Save className="h-4 w-4 mr-2" />{saving ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>

      <ChangeEmailDialog
        open={changeEmailOpen}
        onOpenChange={setChangeEmailOpen}
        user={{ id: client?.client_id, email: liveEmail, first_name: client?.contact_person, last_name: '' }}
        onSaved={(res) => {
          setLiveEmail(res.new_email);
          onSaved?.();
        }}
      />
    </Dialog>
  );
}
